/**
 * Template Builder Page - Visual Template Editor
 * P0-TB1: Editor Freeform com canvas A4, react-konva
 * 
 * Modos:
 * - Classic: Editor de componentes drag-and-drop simples
 * - Freeform: Canvas livre com elementos posicionáveis
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { settingsAPI } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatDateTime } from '@/lib/utils';
import { 
  FileText, Save, Eye, Plus, Trash2, Star, LayoutTemplate, Receipt,
  Download, Upload, Settings, PenTool, Layers, ArrowLeft
} from 'lucide-react';
import FreeformCanvas, { clearTemplateDraft } from '@/components/template-builder/FreeformCanvas';
import { PosStructureEditor } from '@/components/template-builder/PosStructureEditor';
import { TemplateCockpitDiagnosticsPanel } from '@/components/template-builder/TemplateCockpitDiagnosticsPanel';
import { TemplateBuilderReorganizedShell } from '@/components/template-builder/TemplateBuilderReorganizedShell';
import { TemplateDocTypeEditorialPanel } from '@/components/template-builder/TemplateDocTypeEditorialPanel';
import { TemplateStoredEffectiveComparator } from '@/components/template-builder/TemplateStoredEffectiveComparator';
import { A4_DOC_TYPE_EDITORIAL_PROFILES, A4_EDITORIAL_DOC_TYPE_ORDER, isA4EditorialDocType } from '@/lib/a4DocTypeEditorialProfiles';
import { applyPosStructureDraft, ensurePosStructureLayout, extractPosStructureDraft } from '@/components/template-builder/posStructureConfig';
import { EmptyStatePanel } from '@/components/common/EmptyStatePanel';
import { ModuleStateBoundary } from '@/components/common/ModuleStateBoundary';
import { useFetchTelemetry } from '@/hooks/useFetchTelemetry';
import { safeRequest } from '@/lib/safeRequest';
import { buildAuthHeaders, clearStoredAuthSession } from '@/lib/authStorage';
import { applyCleanOrLegacyParam, buildHandleMaps, resolveHandleOrLegacyId } from '@/lib/queryHandles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const redirectToLoginWithReturn = () => {
  clearStoredAuthSession();
  const next = `${window.location.pathname}${window.location.search}`;
  window.location.href = `/login?next=${encodeURIComponent(next)}`;
};

const parseApiResponse = async (response) => {
  let payload = {};
  try {
    payload = await response.json();
  } catch {
    payload = {};
  }

  if (!response.ok) {
    if (response.status === 401) {
      redirectToLoginWithReturn();
    }
    const detail = payload?.detail;
    const message = (typeof detail === 'object' && detail?.message)
      || payload?.message
      || (typeof detail === 'string' ? detail : null)
      || `HTTP error! status: ${response.status}`;
    const error = new Error(message);
    error.payload = payload;
    error.status = response.status;
    throw error;
  }

  return payload;
};

const parsePdfPreviewMeta = (response) => {
  const parseJsonHeader = (key) => {
    const value = response.headers.get(key);
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  return {
    resolvedTemplateId: response.headers.get('X-Resolved-Template-Id') || '',
    resolvedTemplateName: response.headers.get('X-Resolved-Template-Name') || '',
    resolvedTemplateRule: response.headers.get('X-Resolved-Template-Rule') || '',
    resolvedDocType: response.headers.get('X-Resolved-Doc-Type') || '',
    resolvedModuleGroup: response.headers.get('X-Resolved-Module-Group') || '',
    runtimeLocationId: response.headers.get('X-Runtime-Location-Id') || '',
    dataSource: response.headers.get('X-Preview-Data-Source') || '',
    layoutSource: response.headers.get('X-Layout-Source') || '',
    renderPipeline: response.headers.get('X-Render-Pipeline') || '',
    printSettings: parseJsonHeader('X-Print-Settings'),
    validationWarnings: parseJsonHeader('X-Validation-Warnings') || [],
  };
};

// API adapter for templates with blob support for PDF
const api = {
  get: (url) => fetch(`${API_URL}${url}`, {
    headers: buildAuthHeaders()
  }).then(parseApiResponse),
  post: (url, data) => fetch(`${API_URL}${url}`, {
    method: 'POST',
    headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(data)
  }).then(parseApiResponse),
  put: (url, data) => fetch(`${API_URL}${url}`, {
    method: 'PUT',
    headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(data)
  }).then(parseApiResponse),
  delete: (url) => fetch(`${API_URL}${url}`, {
    method: 'DELETE',
    headers: buildAuthHeaders()
  }).then(parseApiResponse),
  // P0-TB1: Authenticated PDF fetch
  postPDF: async (url, data) => {
    const response = await fetch(`${API_URL}${url}`, {
      method: 'POST',
      headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.blob();
  },
  postPDFWithMeta: async (url, data) => {
    const response = await fetch(`${API_URL}${url}`, {
      method: 'POST',
      headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return {
      blob: await response.blob(),
      meta: parsePdfPreviewMeta(response),
    };
  }
};

// Document types configuration
const DOCUMENT_TYPES = [
  { value: 'pos_receipt', label: 'Recibo POS (Térmica)', icon: Receipt, category: 'POS' },
  { value: 'quotation', label: 'Cotação', icon: FileText, category: 'A4' },
  { value: 'invoice', label: 'Factura', icon: FileText, category: 'A4' },
  { value: 'proforma', label: 'Factura Proforma', icon: FileText, category: 'A4' },
  { value: 'delivery_guide', label: 'Guia de Entrega', icon: Download, category: 'A4' },
  { value: 'cash_sale', label: 'Venda a Dinheiro', icon: FileText, category: 'A4' },
  { value: 'credit_note', label: 'Nota de Crédito', icon: FileText, category: 'A4' },
  { value: 'cancelled_sale', label: 'Documento Anulado', icon: FileText, category: 'A4' },
  { value: 'reception_guide', label: 'Guia de Recepção', icon: Upload, category: 'A4' },
  { value: 'service_guide', label: 'Guia de Serviço (Reparação)', icon: Settings, category: 'A4' },
  { value: 'payment_receipt', label: 'Recibo de Pagamento', icon: Receipt, category: 'A4' },
  { value: 'receipt', label: 'Recibo AR', icon: Receipt, category: 'A4' },
  { value: 'purchase_order', label: 'Ordem de Compra', icon: FileText, category: 'A4' },
  { value: 'stock_transfer', label: 'Guia de Transferência', icon: FileText, category: 'A4' },
  { value: 'pos_gaming', label: 'Recibo POS Gaming', icon: Receipt, category: 'POS' },
];

const MODULE_GROUPS = [
  { value: 'VENDAS', label: 'VENDAS', slug: 'vendas', badgeClass: 'bg-blue-100 text-blue-800' },
  { value: 'DOCUMENTOS', label: 'DOCUMENTOS', slug: 'documentos', badgeClass: 'bg-rose-100 text-rose-800' },
  { value: 'SERVIÇOS', label: 'SERVIÇOS', slug: 'servicos', badgeClass: 'bg-emerald-100 text-emerald-800' },
  { value: 'CONTAS_A_RECEBER', label: 'CONTAS_A_RECEBER', slug: 'contas-a-receber', badgeClass: 'bg-amber-100 text-amber-800' },
  { value: 'COMPRAS', label: 'COMPRAS', slug: 'compras', badgeClass: 'bg-indigo-100 text-indigo-800' },
  { value: 'STOCK', label: 'STOCK', slug: 'stock', badgeClass: 'bg-cyan-100 text-cyan-800' },
  { value: 'GAMING', label: 'GAMING', slug: 'gaming', badgeClass: 'bg-fuchsia-100 text-fuchsia-800' },
  { value: 'CAIXA', label: 'CAIXA', slug: 'caixa', badgeClass: 'bg-orange-100 text-orange-800' },
  { value: 'OUTROS', label: 'OUTROS', slug: 'outros', badgeClass: 'bg-slate-100 text-slate-700' },
];

const DOC_TYPE_DEFAULT_MODULE = {
  pos_receipt: 'VENDAS',
  invoice: 'VENDAS',
  proforma: 'VENDAS',
  delivery_guide: 'VENDAS',
  cash_sale: 'VENDAS',
  credit_note: 'VENDAS',
  cancelled_sale: 'VENDAS',
  quotation: 'VENDAS',
  reception_guide: 'SERVIÇOS',
  service_guide: 'SERVIÇOS',
  payment_receipt: 'SERVIÇOS',
  receipt: 'CONTAS_A_RECEBER',
  purchase_order: 'COMPRAS',
  stock_transfer: 'STOCK',
  pos_gaming: 'GAMING',
};

const getDefaultModuleGroupForDocType = (docType) => DOC_TYPE_DEFAULT_MODULE[docType] || 'OUTROS';

const MODULE_GROUP_ALIAS_MAP = {
  VENDAS: 'VENDAS',
  DOCUMENTOS: 'DOCUMENTOS',
  COTACOES: 'DOCUMENTOS',
  SERVICOS: 'SERVIÇOS',
  SERVIÇOS: 'SERVIÇOS',
  CONTAS_A_RECEBER: 'CONTAS_A_RECEBER',
  AR: 'CONTAS_A_RECEBER',
  COMPRAS: 'COMPRAS',
  STOCK: 'STOCK',
  GAMING: 'GAMING',
  CAIXA: 'CAIXA',
  OUTRO: 'OUTROS',
  OUTROS: 'OUTROS',
};

const normalizeModuleKey = (value = '') => String(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toUpperCase()
  .replace(/[\s/-]+/g, '_')
  .replace(/_+/g, '_');

const getCanonicalModuleValue = (value) => MODULE_GROUP_ALIAS_MAP[normalizeModuleKey(value)] || null;

const normalizeModuleGroup = (value, fallbackDocType = null) => {
  const source = Array.isArray(value)
    ? value
    : typeof value === 'string' && value.trim()
      ? [value]
      : [];

  const normalized = source
    .map((item) => getCanonicalModuleValue(item))
    .filter((item, index, arr) => item && arr.indexOf(item) === index);

  if (normalized.length > 0) return [normalized[0]];
  return [getDefaultModuleGroupForDocType(fallbackDocType)];
};

const getDocumentLabel = (docType) => {
  const match = DOCUMENT_TYPES.find((item) => item.value === docType);
  if (match) return match.label;
  return String(docType || 'N/A')
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const MANUAL_TEMPLATE_DECISIONS = {
  '3634445b-5de2-4334-8adb-f49480cf5699': {
    scope: 'invoice | Global | VENDAS',
    decision: 'keep',
    label: 'Manter como padrão',
    counterpartId: 'template-invoice-default',
    counterpartName: 'FACTURA - VENDAS - Alternativo 2',
    note: 'Decisão manual aprovada. Não apagar automaticamente nesta ronda.',
  },
  'template-invoice-default': {
    scope: 'invoice | Global | VENDAS',
    decision: 'redundant',
    label: 'Deixar como redundante',
    counterpartId: '3634445b-5de2-4334-8adb-f49480cf5699',
    counterpartName: 'FACTURA - VENDAS',
    note: 'Preparado para revisão manual posterior. Não apagar automaticamente nesta ronda.',
  },
  '38e5bf30-abf5-486c-8419-8f60918fe445': {
    scope: 'quotation | Global | DOCUMENTOS',
    decision: 'keep',
    label: 'Manter como padrão',
    counterpartId: 'template-quotation-default',
    counterpartName: 'COTAÇÃO - DOCUMENTOS - Alternativo 2',
    note: 'Decisão manual aprovada. Não apagar automaticamente nesta ronda.',
  },
  'template-quotation-default': {
    scope: 'quotation | Global | DOCUMENTOS',
    decision: 'redundant',
    label: 'Deixar como redundante',
    counterpartId: '38e5bf30-abf5-486c-8419-8f60918fe445',
    counterpartName: 'COTAÇÃO - DOCUMENTOS',
    note: 'Preparado para revisão manual posterior. Não apagar automaticamente nesta ronda.',
  },
};

const getManualTemplateDecision = (template) => {
  if (!template) return null;
  return MANUAL_TEMPLATE_DECISIONS[template.id] || MANUAL_TEMPLATE_DECISIONS[template.name] || null;
};

// Main Template Builder Page
export default function TemplateBuilderPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState('');
  const [selectedTemplateModules, setSelectedTemplateModules] = useState(['OUTROS']);
  const [editorMode, setEditorMode] = useState(null); // null, 'freeform'
  const [posContextOnly, setPosContextOnly] = useState(false);
  const [dedupeLoading, setDedupeLoading] = useState(false);
  const activeModuleFilter = searchParams.get('module_group') || 'ALL';
  const activeDocTypeFilter = searchParams.get('doc_type') || 'ALL';
  const activeLocationFilter = 'GLOBAL';
  const activeSearchFilter = searchParams.get('q') || '';
  const templateHandleMaps = useMemo(() => buildHandleMaps(templates, (template) => template.name || template.id), [templates]);
  const currentTemplateId = useMemo(() => resolveHandleOrLegacyId({
    cleanValue: searchParams.get('template'),
    legacyValue: searchParams.get('template_id'),
    handleToId: templateHandleMaps.handleToId,
  }), [searchParams, templateHandleMaps.handleToId]);

  // Template form state
  const [templateForm, setTemplateForm] = useState({
    name: '',
    document_type: 'quotation',
    is_default: false,
    location_id: null,
    module_group: ['DOCUMENTOS'],
  });
  
  // Dialogs
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetTemplate, setDeleteTargetTemplate] = useState(null);
  const [deleteImpact, setDeleteImpact] = useState(null);
  const [deleteImpactLoading, setDeleteImpactLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [templateDiagnostics, setTemplateDiagnostics] = useState({ summary: {}, duplicate_scopes: [], location_legacy_scopes: [], print_preset_warnings: [] });
  const [shellMode, setShellMode] = useState(() => sessionStorage.getItem('template-builder-shell-mode-v1') || 'reorganized');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMeta, setPreviewMeta] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [templateCompareData, setTemplateCompareData] = useState(null);
  const [templateCompareLoading, setTemplateCompareLoading] = useState(false);
  const latestSelectionRequestRef = useRef(0);
  const pendingSelectedTemplateIdRef = useRef('');
  const { partialIssues, captureFetchTelemetry, resetFetchTelemetry } = useFetchTelemetry();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      resetFetchTelemetry();
      const templateParams = new URLSearchParams({ summary: 'true' });
      if (activeModuleFilter !== 'ALL') {
        templateParams.set('module_group', activeModuleFilter);
      }
      if (activeDocTypeFilter !== 'ALL') {
        templateParams.set('document_type', activeDocTypeFilter);
      }
      const [templatesRes, locationsRes, diagnosticsRes] = await Promise.all([
        safeRequest(() => api.get(`/api/templates/?${templateParams.toString()}`), { templates: [] }, { telemetry: { module: 'template-builder', endpoint: `/api/templates/?${templateParams.toString()}`, onEvent: captureFetchTelemetry } }),
        safeRequest(() => settingsAPI.listLocations(), { data: [] }, { telemetry: { module: 'template-builder', endpoint: 'settings.locations', onEvent: captureFetchTelemetry } }),
        safeRequest(() => api.get('/api/templates/cockpit-diagnostics'), { summary: {}, duplicate_scopes: [], location_legacy_scopes: [], print_preset_warnings: [] }, { telemetry: { module: 'template-builder', endpoint: '/api/templates/cockpit-diagnostics', onEvent: captureFetchTelemetry } }),
      ]);
      
      setTemplates(templatesRes.templates || []);
      setLocations(Array.isArray(locationsRes) ? locationsRes : locationsRes.data || []);
      setTemplateDiagnostics(diagnosticsRes || { summary: {}, duplicate_scopes: [], location_legacy_scopes: [], print_preset_warnings: [] });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [activeDocTypeFilter, activeModuleFilter, captureFetchTelemetry, resetFetchTelemetry]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    sessionStorage.setItem('template-builder-shell-mode-v1', shellMode);
  }, [shellMode]);

  useEffect(() => {
    const context = searchParams.get('context');
    if (context === 'pos') {
      setPosContextOnly(true);
      if (!searchParams.get('module_group')) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('module_group', 'VENDAS');
        setSearchParams(nextParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  const updateSearchFilter = useCallback((key, value) => {
    const nextParams = new URLSearchParams(searchParams);
    if (!value || value === 'ALL') {
      nextParams.delete(key);
    } else {
      nextParams.set(key, value);
    }
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const clearAllFilters = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('module_group');
    nextParams.delete('doc_type');
    nextParams.delete('location_id');
    nextParams.delete('q');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const syncSelectedTemplateState = useCallback((template, options = {}) => {
    const { keepEditorMode = false } = options;
    const normalizedModules = normalizeModuleGroup(template.module_group, template.document_type);
    setSelectedTemplate(template);
    setSelectedTemplateName(template.name || '');
    setSelectedTemplateModules(normalizedModules);
    setTemplateForm({
      name: template.name,
      document_type: template.document_type,
      is_default: template.is_default || false,
      location_id: template.location_id || null,
      module_group: normalizedModules,
    });
    setEditorMode(keepEditorMode ? 'freeform' : null);
  }, []);

  const loadTemplateDetail = useCallback(async (templateId, options = {}) => {
    if (!templateId) return null;
    const requestId = ++latestSelectionRequestRef.current;
    const detail = await safeRequest(
      () => api.get(`/api/templates/${templateId}`),
      null,
      {
        required: true,
        telemetry: {
          module: 'template-builder',
          endpoint: `/api/templates/${templateId}`,
          onEvent: captureFetchTelemetry,
        },
      },
    );
    if (detail && requestId === latestSelectionRequestRef.current) {
      syncSelectedTemplateState(detail, options);
      pendingSelectedTemplateIdRef.current = '';
    }
    return detail;
  }, [captureFetchTelemetry, syncSelectedTemplateState]);

  const handleSelectTemplate = useCallback(async (template, options = {}) => {
    pendingSelectedTemplateIdRef.current = template.id;
    syncSelectedTemplateState(template, options);
    if (currentTemplateId !== template.id) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('template');
      nextParams.delete('template_id');
      applyCleanOrLegacyParam({
        params: nextParams,
        cleanKey: 'template',
        legacyKey: 'template_id',
        value: template.id,
        idToHandle: templateHandleMaps.idToHandle,
      });
      setSearchParams(nextParams, { replace: true });
    }
    try {
      await loadTemplateDetail(template.id, options);
    } catch (error) {
      toast.error(error?.message || 'Erro ao carregar detalhe do template');
    }
  }, [currentTemplateId, loadTemplateDetail, searchParams, setSearchParams, syncSelectedTemplateState, templateHandleMaps.idToHandle]);

  const getConformedTemplateLayout = useCallback((template, sourceLayout = null) => {
    if (!template) return sourceLayout || null;
    const baseLayout = sourceLayout || template.freeform_layout;
    if (!baseLayout) return null;
    if (!['pos_receipt', 'pos_gaming'].includes(template.document_type)) return baseLayout;
    return applyPosStructureDraft(
      ensurePosStructureLayout(baseLayout),
      extractPosStructureDraft(baseLayout),
    );
  }, []);

  const openFreeformEditor = useCallback((template = null) => {
    const targetTemplate = template || selectedTemplate;
    if (!targetTemplate) return;
    const conformedTemplate = targetTemplate?.freeform_layout
      ? { ...targetTemplate, freeform_layout: getConformedTemplateLayout(targetTemplate, targetTemplate.freeform_layout) }
      : targetTemplate;
    syncSelectedTemplateState(conformedTemplate, { keepEditorMode: true });
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('template');
    nextParams.delete('template_id');
    applyCleanOrLegacyParam({
      params: nextParams,
      cleanKey: 'template',
      legacyKey: 'template_id',
      value: targetTemplate.id,
      idToHandle: templateHandleMaps.idToHandle,
    });
    nextParams.set('editor', 'freeform');
    setSearchParams(nextParams, { replace: true });
    setEditorMode('freeform');
  }, [getConformedTemplateLayout, searchParams, selectedTemplate, setSearchParams, syncSelectedTemplateState, templateHandleMaps.idToHandle]);

  const closeEditorMode = useCallback(() => {
    setEditorMode(null);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('editor');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!currentTemplateId || templates.length === 0) return;
    if (pendingSelectedTemplateIdRef.current && pendingSelectedTemplateIdRef.current !== currentTemplateId) return;
    if (selectedTemplate?.id && selectedTemplate.id !== currentTemplateId) return;
    const templateFromUrl = templates.find((template) => template.id === currentTemplateId);
    if (!templateFromUrl) {
      if (editorMode !== 'freeform' && templates.length > 0) {
        handleSelectTemplate(templates[0], { keepEditorMode: false }).catch(() => {
          toast.error('Erro ao sincronizar template com o filtro activo');
        });
      }
      return;
    }

    const requiresDetail = (
      templateFromUrl.id !== selectedTemplate?.id
      || templateFromUrl.updated_at !== selectedTemplate?.updated_at
      || !selectedTemplate?.freeform_layout?.elements?.length
    );

    if (requiresDetail) {
      loadTemplateDetail(currentTemplateId, { keepEditorMode: editorMode === 'freeform' }).catch(() => {
        // erro tratado no fluxo explícito
      });
    }
  }, [currentTemplateId, editorMode, handleSelectTemplate, loadTemplateDetail, selectedTemplate?.freeform_layout?.elements?.length, selectedTemplate?.id, selectedTemplate?.updated_at, templates]);

  useEffect(() => {
    if (pendingSelectedTemplateIdRef.current && pendingSelectedTemplateIdRef.current === currentTemplateId) {
      pendingSelectedTemplateIdRef.current = '';
    }
  }, [currentTemplateId]);

  useEffect(() => {
    const editorParam = searchParams.get('editor');
    if (editorParam === 'freeform' && selectedTemplate && editorMode !== 'freeform') {
      setEditorMode('freeform');
    }
    if (editorParam !== 'freeform' && editorMode === 'freeform') {
      setEditorMode(null);
    }
  }, [searchParams, selectedTemplate, editorMode]);

  const handleCreateTemplate = async () => {
    if (!templateForm.name || !templateForm.document_type) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }

    try {
      const data = {
        ...templateForm,
        document_type: templateForm.document_type,
        module_group: normalizeModuleGroup(templateForm.module_group, templateForm.document_type),
        freeform_layout: null,
      };
      
      const response = await api.post('/api/templates/', data);
      
      if (response.id) {
        toast.success('Template criado com sucesso');
        setCreateDialogOpen(false);
        loadData();
        handleSelectTemplate(response);
      }
    } catch (error) {
      const message = error?.message || '';
      if (message.includes('scope_conflict') || message.includes('template global') || message.includes('409')) {
        toast.error('Já existe um template global activo para este tipo de documento. Edite o template existente.');
      } else {
        toast.error('Erro ao criar template');
      }
    }
  };

  const handleRunHardDeduplication = async () => {
    setDedupeLoading(true);
    try {
      const response = await api.post('/api/templates/deduplicate-hard-delete', {});
      toast.success(`Deduplicação concluída: ${response.templates_hard_deleted || 0} removidos, ${response.templates_kept || 0} mantidos`);
      await loadData();
    } catch (error) {
      toast.error('Erro ao consolidar templates duplicados');
    } finally {
      setDedupeLoading(false);
    }
  };

  const handleSaveLayout = async (layout) => {
    if (!selectedTemplate) return;
    const layoutToSave = getConformedTemplateLayout(selectedTemplate, layout);

    try {
      const response = await api.put(`/api/templates/${selectedTemplate.id}/freeform-layout`, layoutToSave);
      clearTemplateDraft(selectedTemplate.id);
      toast.success('Layout guardado com sucesso');

      const updatedTemplate = response?.template || response?.data?.template || {
        ...selectedTemplate,
        freeform_layout: layoutToSave,
        updated_at: response?.updated_at || response?.data?.updated_at || new Date().toISOString(),
      };

      setTemplates((prev) => prev.map((template) => (
        template.id === updatedTemplate.id
          ? { ...template, ...updatedTemplate, freeform_layout: layoutToSave }
          : template
      )));
      syncSelectedTemplateState({ ...updatedTemplate, freeform_layout: layoutToSave }, { keepEditorMode: true });

      if (previewDialogOpen) {
        await handlePreviewPDF(layoutToSave);
      }

      loadData();
    } catch (error) {
      if (error?.status === 401) {
        toast.error('Sessão expirada. O layout ficou preservado localmente. Volte a entrar para gravar sem perder alterações.');
        throw error;
      }
      toast.error(error?.message || 'Erro ao guardar layout');
      console.error(error);
      throw error;
    }
  };

  const handlePreviewPDF = useCallback(async (layout, options = {}) => {
    const { openDialog = true, showToast = true, templateOverride = null, useStoredLayout = false } = options;
    const templateForPreview = templateOverride || selectedTemplate;
    if (!templateForPreview?.id) return;

    setPreviewLoading(true);
    setPreviewError('');
    if (openDialog) {
      setPreviewDialogOpen(true);
    }

    try {
      const conformedLayout = useStoredLayout
        ? null
        : getConformedTemplateLayout(templateForPreview, layout || templateForPreview?.freeform_layout);
      const { blob, meta } = await api.postPDFWithMeta('/api/templates/render-pdf', {
        template_id: templateForPreview.id,
        doc_type: templateForPreview.document_type,
        location_id: templateForPreview.location_id || null,
        module_group: getTemplateModules(templateForPreview)[0],
        copy_type: 'Original',
        ...(conformedLayout ? { layout_override: conformedLayout } : {}),
      });

      const url = URL.createObjectURL(blob);
      setPreviewUrl((previousUrl) => {
        if (previousUrl) {
          URL.revokeObjectURL(previousUrl);
        }
        return url;
      });
      setPreviewMeta({
        ...meta,
        sourceTemplateId: templateForPreview.id,
        loadedAt: new Date().toISOString(),
      });
    } catch (error) {
      setPreviewMeta(null);
      setPreviewUrl((previousUrl) => {
        if (previousUrl) {
          URL.revokeObjectURL(previousUrl);
        }
        return null;
      });
      setPreviewError('Erro ao gerar pré-visualização');
      if (showToast) {
        toast.error('Erro ao gerar pré-visualização');
      }
      console.error(error);
    } finally {
      setPreviewLoading(false);
    }
  }, [selectedTemplate]);

  useEffect(() => {
    if (shellMode !== 'reorganized' || !selectedTemplate?.id) return;
    const selectedLayout = getConformedTemplateLayout(selectedTemplate);
    if (!(selectedLayout?.elements || []).length) {
      setPreviewMeta(null);
      setPreviewError('Este template ainda não tem layout utilizável para preview.');
      setPreviewUrl((previousUrl) => {
        if (previousUrl) {
          URL.revokeObjectURL(previousUrl);
        }
        return null;
      });
      return;
    }
    handlePreviewPDF(null, { openDialog: false, showToast: false, templateOverride: selectedTemplate, useStoredLayout: true });
  }, [handlePreviewPDF, selectedTemplate?.id, selectedTemplate?.updated_at, shellMode]);

  useEffect(() => {
    let cancelled = false;

    const loadTemplateCompare = async () => {
      if (!selectedTemplate?.id) {
        setTemplateCompareData(null);
        return;
      }

      setTemplateCompareLoading(true);
      try {
        const locationQuery = selectedTemplate.location_id ? `?location_id=${encodeURIComponent(selectedTemplate.location_id)}` : '';
        const data = await api.get(`/api/templates/compare-resolution/${selectedTemplate.id}${locationQuery}`);
        if (!cancelled) {
          setTemplateCompareData(data);
        }
      } catch (error) {
        if (!cancelled) {
          setTemplateCompareData(null);
        }
        console.error('Erro ao carregar comparador do template:', error);
      } finally {
        if (!cancelled) {
          setTemplateCompareLoading(false);
        }
      }
    };

    loadTemplateCompare();
    return () => {
      cancelled = true;
    };
  }, [selectedTemplate?.id, selectedTemplate?.location_id]);

  const handleSetDefault = async (templateId) => {
    try {
      await api.put(`/api/templates/${templateId}/set-default`, {});
      toast.success('Template definido como padrão');
      loadData();
    } catch (error) {
      toast.error('Erro ao definir template padrão');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;

    setDeleteTargetTemplate(template);
    setDeleteDialogOpen(true);
    setDeleteImpactLoading(true);
    setDeleteImpact(null);

    try {
      const impact = await api.get(`/api/templates/${templateId}/delete-impact`);
      setDeleteImpact(impact || null);
    } catch (error) {
      setDeleteImpact({
        documents_count: null,
        documents_count_deterministic: false,
        modules_count: null,
      });
    } finally {
      setDeleteImpactLoading(false);
    }
  };

  const handleConfirmDeleteTemplate = async () => {
    if (!deleteTargetTemplate) return;

    try {
      const response = await fetch(`${API_URL}/api/templates/${deleteTargetTemplate.id}`, {
        method: 'DELETE',
        headers: buildAuthHeaders()
      });

      let payload = {};
      try {
        payload = await response.clone().json();
      } catch {
        try {
          payload = { message: await response.text() };
        } catch {
          payload = {};
        }
      }

      if (!response.ok) {
        const detail = payload?.detail;
        const message = (typeof detail === 'object' && detail?.message)
          || payload?.message
          || (typeof detail === 'string' ? detail : null)
          || (response.status === 409
            ? 'Crie uma alternativa primeiro.'
            : 'Erro ao eliminar template');
        throw new Error(message);
      }

      const promotedTemplateId = payload?.promoted_default_template_id;
      toast.success(
        promotedTemplateId
          ? 'Template eliminado e default redefinido automaticamente'
          : 'Template eliminado'
      );
      if (selectedTemplate?.id === deleteTargetTemplate.id) {
        setSelectedTemplate(null);
        closeEditorMode();
      }
      setDeleteDialogOpen(false);
      setDeleteTargetTemplate(null);
      setDeleteImpact(null);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Erro ao eliminar template');
    }
  };

  const handleReplaceTemplateFlow = () => {
    const targetTemplate = deleteTargetTemplate || selectedTemplate;
    if (!targetTemplate) return;
    handleSelectTemplate(targetTemplate);
    setDeleteDialogOpen(false);
    setDeleteTargetTemplate(null);
    setDeleteImpact(null);
    openFreeformEditor(targetTemplate);
    toast.info('Modo Substituir Template aberto. Edite e guarde este template para o substituir sem quebrar o escopo único.');
  };

  const handleSaveTemplateModuleGroup = async () => {
    if (!selectedTemplate) return;
    const normalizedModules = normalizeModuleGroup(selectedTemplateModules, selectedTemplate.document_type);

    try {
      await api.put(`/api/templates/${selectedTemplate.id}`, { module_group: normalizedModules });
      toast.success('Módulo do template guardado');
      setSelectedTemplate((prev) => ({ ...prev, module_group: normalizedModules }));
      loadData();
    } catch (error) {
      if (String(error?.message || '').includes('template global')) {
        toast.error('Este doc_type já está reservado ao template global actual.');
      } else {
        toast.error('Erro ao guardar módulo do template');
      }
    }
  };

  const handleSaveTemplateName = async () => {
    if (!selectedTemplate) return;
    const trimmedName = selectedTemplateName.trim();

    if (!trimmedName) {
      toast.error('Introduza um nome válido para o template');
      return;
    }

    try {
      const updated = await api.put(`/api/templates/${selectedTemplate.id}`, { name: trimmedName });
      toast.success('Nome do template guardado');
      setSelectedTemplate((prev) => ({ ...prev, ...updated }));
      setSelectedTemplateName(updated.name || trimmedName);
      loadData();
    } catch (error) {
      toast.error(error?.message || 'Erro ao guardar nome do template');
    }
  };

  // Cleanup preview URL
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const moduleOrder = MODULE_GROUPS.reduce((acc, group, index) => {
    acc[group.value] = index;
    return acc;
  }, {});

  const getModuleMeta = (moduleValue) => {
    return MODULE_GROUPS.find((group) => group.value === moduleValue) || MODULE_GROUPS.find((group) => group.value === 'OUTROS');
  };

  const getTemplateModules = useCallback((template) => normalizeModuleGroup(template?.module_group, template?.document_type), []);
  const getPrimaryTemplateModule = useCallback((template) => template?.origin_context || template?.primaryModule || getTemplateModules(template)[0], [getTemplateModules]);
  const getTemplateDecisionGuidance = useCallback((template) => getManualTemplateDecision(template), []);
  const getResolvedTemplateName = useCallback((template, draftName = '') => {
    if (draftName && draftName.trim()) return draftName.trim();
    return template?.name || getDocumentLabel(template?.document_type);
  }, []);
  const getResolvedTemplateModule = useCallback((template, draftModules = null) => {
    if (!template) return 'OUTROS';
    return normalizeModuleGroup(draftModules || template.module_group, template.document_type)[0];
  }, []);
  const getResolvedTemplateSignature = useCallback((template, draftName = '', draftModules = null) => {
    if (!template) return 'Template';
    const resolvedName = getResolvedTemplateName(template, draftName);
    const resolvedModule = getResolvedTemplateModule(template, draftModules);
    return `${resolvedName} · ${getDocumentLabel(template.document_type)} · ${resolvedModule} · GLOBAL`;
  }, [getResolvedTemplateModule, getResolvedTemplateName]);
  const isTemplateUnlinked = (template) => getPrimaryTemplateModule(template) === 'OUTROS';

  const getLocationName = useCallback((template) => {
    if (template.location_name) return template.location_name;
    if (!template.location_id) return 'Global';
    const loc = locations.find((l) => l.id === template.location_id);
    return loc?.name || 'Global';
  }, [locations]);

  const getTemplateDisplayOptionLabel = useCallback((template) => {
    if (!template) return 'Template';
    return template.name || getDocumentLabel(template.document_type);
  }, []);

  const getBranchLabel = (name) => {
    const lower = (name || '').toLowerCase();
    if (lower.includes('maputo') || lower.includes('central') || lower.includes('sede')) return 'SEDE';
    if (lower.includes('matola') || lower.includes('sucursal') || lower.includes('novare')) return 'SUCURSAL';
    return 'GLOBAL';
  };

  const getBranchOrder = (name) => {
    const label = getBranchLabel(name);
    if (label === 'SEDE') return 0;
    if (label === 'SUCURSAL') return 1;
    return 2;
  };

  const documentTypeOptions = useMemo(() => {
    const items = new Map();
    DOCUMENT_TYPES.forEach((item) => {
      items.set(item.value, { value: item.value, label: item.label });
    });
    templates.forEach((template) => {
      if (!items.has(template.document_type)) {
        items.set(template.document_type, {
          value: template.document_type,
          label: getDocumentLabel(template.document_type),
        });
      }
    });
    return Array.from(items.values()).sort((a, b) => a.label.localeCompare(b.label, 'pt'));
  }, [templates]);

  const documentTypeOrder = documentTypeOptions.reduce((acc, item, index) => {
    acc[item.value] = index;
    return acc;
  }, {});

  const sortTemplates = (a, b) => {
    const aLocName = getLocationName(a);
    const bLocName = getLocationName(b);
    const aModuleOrder = moduleOrder[getPrimaryTemplateModule(a)] ?? 999;
    const bModuleOrder = moduleOrder[getPrimaryTemplateModule(b)] ?? 999;
    const aDocTypeOrder = documentTypeOrder[a.document_type] ?? 999;
    const bDocTypeOrder = documentTypeOrder[b.document_type] ?? 999;
    const aBranch = getBranchOrder(aLocName);
    const bBranch = getBranchOrder(bLocName);
    const aActive = a.is_active === false ? 1 : 0;
    const bActive = b.is_active === false ? 1 : 0;
    const aDefault = a.is_default ? 0 : 1;
    const bDefault = b.is_default ? 0 : 1;
    const aUpdated = new Date(a.updated_at || a.created_at || 0).getTime();
    const bUpdated = new Date(b.updated_at || b.created_at || 0).getTime();

    return (
      aModuleOrder - bModuleOrder ||
      aDocTypeOrder - bDocTypeOrder ||
      aBranch - bBranch ||
      aActive - bActive ||
      aDefault - bDefault ||
      bUpdated - aUpdated
    );
  };

  const filteredTemplates = templates.filter((template) => {
    if (posContextOnly && !['pos_receipt', 'pos_gaming'].includes(template.document_type)) {
      return false;
    }

    if (activeModuleFilter !== 'ALL' && getPrimaryTemplateModule(template) !== activeModuleFilter) {
      return false;
    }

    if (activeDocTypeFilter !== 'ALL' && template.document_type !== activeDocTypeFilter) {
      return false;
    }

    if (activeSearchFilter.trim()) {
      const query = activeSearchFilter.trim().toLowerCase();
      const haystack = [
        template.name,
        template.document_type,
        getDocumentLabel(template.document_type),
        getLocationName(template),
        getPrimaryTemplateModule(template),
        template.id,
      ].some((value) => String(value || '').toLowerCase().includes(query));
      if (!haystack) {
        return false;
      }
    }

    return true;
  });

  const allTemplatesSorted = [...templates].sort(sortTemplates);
  const sortedTemplates = [...filteredTemplates].sort(sortTemplates);
  const templateStats = useMemo(() => ({
    total: templates.length,
    active: templates.filter((template) => template.is_active !== false).length,
    defaults: templates.filter((template) => template.is_default).length,
    visible: sortedTemplates.length,
    docTypes: new Set(templates.map((template) => template.document_type).filter(Boolean)).size,
    scopes: new Set(templates.map((template) => template.location_id || 'GLOBAL')).size,
  }), [templates, sortedTemplates.length]);
  const diagnosticsSummary = templateDiagnostics?.summary || {};

  useEffect(() => {
    if (editorMode === 'freeform') return;
    if (selectedTemplate?.id || sortedTemplates.length === 0) return;
    if (currentTemplateId) return;
    const nextTemplate = sortedTemplates[0];
    handleSelectTemplate(nextTemplate).catch(() => {
      toast.error('Erro ao seleccionar template inicial');
    });
  }, [currentTemplateId, editorMode, handleSelectTemplate, selectedTemplate?.id, sortedTemplates]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // Freeform Editor Mode
  if (editorMode === 'freeform' && selectedTemplate) {
    return (
      <div className="flex h-[calc(100vh-100px)] min-h-0 flex-col" data-testid="template-builder-freeform">
        <div className="mb-4 flex shrink-0 flex-wrap items-start gap-4 px-4 xl:flex-nowrap">
          <Button 
            variant="ghost" 
            onClick={closeEditorMode}
            data-testid="back-to-templates-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
          </Button>
          <div>
            <h1 className="text-lg font-bold">{selectedTemplate.name}</h1>
            <p className="text-sm text-muted-foreground">
              Editor Freeform - {getDocumentLabel(selectedTemplate.document_type)}
            </p>
          </div>
          <div className="w-full xl:ml-auto xl:max-w-xl">
            <Label className="mb-2 block text-xs text-muted-foreground">Selecionar template manualmente no editor</Label>
            <Select
              value={selectedTemplate.id}
              onValueChange={(value) => {
                const template = allTemplatesSorted.find((item) => item.id === value) || templates.find((item) => item.id === value);
                if (template) {
                  handleSelectTemplate(template, { keepEditorMode: true });
                }
              }}
            >
              <SelectTrigger data-testid="freeform-template-selector">
                <SelectValue placeholder="Selecionar template" />
              </SelectTrigger>
              <SelectContent>
                {allTemplatesSorted.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {getTemplateDisplayOptionLabel(template)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="min-h-0 flex-1">
          <FreeformCanvas
            templateId={selectedTemplate.id}
            initialLayout={getConformedTemplateLayout(selectedTemplate)}
            templateUpdatedAt={selectedTemplate.updated_at || selectedTemplate.created_at}
            availableTemplates={allTemplatesSorted}
            onSave={handleSaveLayout}
            onPreview={handlePreviewPDF}
          />
        </div>
        
        {/* Preview Dialog */}
        <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
          <DialogContent className="max-w-5xl h-[90vh]">
            <DialogHeader>
              <DialogTitle>Pré-visualização PDF</DialogTitle>
              <DialogDescription>
                {selectedTemplate?.name} - {previewLoading ? 'A gerar...' : 'Documento gerado com dados de exemplo'}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 h-full min-h-[500px]">
              {previewLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
                </div>
              ) : previewUrl ? (
                <iframe
                  src={previewUrl}
                  className="w-full h-full border rounded"
                  title="PDF Preview"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  Erro ao carregar pré-visualização
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Template Selection Mode
  return (
    <div className="h-full p-4" data-testid="template-builder-page">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold">Template Builder</h1>
          <p className="text-sm text-muted-foreground">Centro de controlo com um único template global por tipo de documento, usando injecção dinâmica da loja da sessão no momento da impressão.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={handleRunHardDeduplication} disabled={dedupeLoading} data-testid="run-template-hard-dedup-btn">
            {dedupeLoading ? 'A consolidar...' : 'Consolidar duplicados'}
          </Button>
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="create-template-btn">
            <Plus className="h-4 w-4 mr-2" />
            Novo Template
          </Button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-start gap-1.5 max-w-[980px]" data-testid="template-builder-summary-cards">
        <Card className="w-[144px] shadow-none" data-testid="template-summary-total-card">
          <CardContent className="flex h-[58px] items-center justify-between gap-2 px-3 py-1.5">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Templates registados</p>
            <p className="text-lg font-bold leading-none">{templateStats.total}</p>
          </CardContent>
        </Card>
        <Card className="w-[144px] shadow-none" data-testid="template-summary-active-card">
          <CardContent className="flex h-[58px] items-center justify-between gap-2 px-3 py-1.5">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Activos</p>
            <p className="text-lg font-bold leading-none">{templateStats.active}</p>
          </CardContent>
        </Card>
        <Card className="w-[144px] shadow-none" data-testid="template-summary-default-card">
          <CardContent className="flex h-[58px] items-center justify-between gap-2 px-3 py-1.5">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Padrão</p>
            <p className="text-lg font-bold leading-none">{templateStats.defaults}</p>
          </CardContent>
        </Card>
        <Card className="w-[144px] shadow-none" data-testid="template-summary-doc-types-card">
          <CardContent className="flex h-[58px] items-center justify-between gap-2 px-3 py-1.5">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Doc types cobertos</p>
            <p className="text-lg font-bold leading-none">{templateStats.docTypes}</p>
          </CardContent>
        </Card>
        <Card className="w-[144px] shadow-none" data-testid="template-summary-visible-card">
          <CardContent className="flex h-[58px] items-center justify-between gap-2 px-3 py-1.5">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Visíveis com filtros</p>
            <p className="text-lg font-bold leading-none">{templateStats.visible}</p>
          </CardContent>
        </Card>
        <Card className="w-[144px] shadow-none" data-testid="template-summary-physical-card">
          <CardContent className="flex h-[58px] items-center justify-between gap-2 px-3 py-1.5">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Físicos BD</p>
            <p className="text-lg font-bold leading-none">{diagnosticsSummary.physical_templates || 0}</p>
          </CardContent>
        </Card>
        <Card className="w-[144px] shadow-none" data-testid="template-summary-effective-card">
          <CardContent className="flex h-[58px] items-center justify-between gap-2 px-3 py-1.5">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Efectivos</p>
            <p className="text-lg font-bold leading-none">{diagnosticsSummary.effective_templates || 0}</p>
          </CardContent>
        </Card>
        <Card className="w-[144px] shadow-none" data-testid="template-summary-duplicates-card">
          <CardContent className="flex h-[58px] items-center justify-between gap-2 px-3 py-1.5">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Duplicados</p>
            <p className="text-lg font-bold leading-none">{diagnosticsSummary.duplicate_scopes || 0}</p>
          </CardContent>
        </Card>
        <Card className="w-[144px] shadow-none" data-testid="template-summary-legacy-card">
          <CardContent className="flex h-[58px] items-center justify-between gap-2 px-3 py-1.5">
            <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Legacy local</p>
            <p className="text-lg font-bold leading-none">{diagnosticsSummary.location_legacy_variants || 0}</p>
          </CardContent>
        </Card>
      </div>

      {shellMode === 'classic' && (
        <div className="mb-4" data-testid="template-builder-diagnostics-section">
          <TemplateCockpitDiagnosticsPanel
            diagnostics={templateDiagnostics}
            title="Fase 2A — observabilidade do cockpit"
            description="Estado físico, efectivo, duplicações e legado visíveis antes da limpeza estrutural."
            testIdPrefix="template-builder-diagnostics"
          />
        </div>
      )}

      <div className="mb-4 rounded-xl border bg-slate-50 px-3 py-3" data-testid="template-builder-shell-switcher-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold">Cockpit actual vs cockpit reorganizado</p>
            <p className="text-[11px] text-muted-foreground">Transição segura: o shell novo de 3 painéis corre sem destruir o cockpit clássico.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant={shellMode === 'reorganized' ? 'default' : 'outline'} size="sm" onClick={() => setShellMode('reorganized')} data-testid="template-builder-shell-reorganized-button">
              Cockpit reorganizado
            </Button>
            <Button variant={shellMode === 'classic' ? 'default' : 'outline'} size="sm" onClick={() => setShellMode('classic')} data-testid="template-builder-shell-classic-button">
              Cockpit actual
            </Button>
          </div>
        </div>
      </div>

      {posContextOnly && (
        <div className="mb-3 rounded-lg border bg-amber-50 p-2.5" data-testid="template-builder-pos-context-banner">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium text-amber-900">Contexto POS ativo</p>
              <p className="text-xs text-amber-800">
                Abertura via Configurações &gt; Impressões. A lista está focada em templates POS globais por tipo de documento.
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPosContextOnly(false)}
              data-testid="template-builder-show-all-doc-types-btn"
            >
              Mostrar A4 / Todos
            </Button>
          </div>
        </div>
      )}

      {!posContextOnly && (
        <div className="mb-4 rounded-xl border bg-white px-3 py-3" data-testid="template-builder-a4-editorial-focus-card">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold">Percurso editorial A4 por `doc_type`</p>
              <p className="text-[11px] text-muted-foreground">Nesta ronda tratamos `invoice`, `quotation`, `delivery_guide`, `credit_note`, `receipt` e `payment_receipt` sem abrir deduplicação destrutiva.</p>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="template-builder-a4-editorial-focus-buttons">
              {A4_EDITORIAL_DOC_TYPE_ORDER.map((docType) => (
                <Button
                  key={`editorial-focus-${docType}`}
                  size="sm"
                  variant={activeDocTypeFilter === docType ? 'default' : 'outline'}
                  onClick={() => updateSearchFilter('doc_type', docType)}
                  data-testid={`template-builder-a4-editorial-focus-${docType}`}
                >
                  {A4_DOC_TYPE_EDITORIAL_PROFILES[docType]?.label || docType}
                </Button>
              ))}
              <Button
                size="sm"
                variant={activeDocTypeFilter === 'ALL' ? 'default' : 'ghost'}
                onClick={() => updateSearchFilter('doc_type', 'ALL')}
                data-testid="template-builder-a4-editorial-focus-reset"
              >
                Ver todos
              </Button>
            </div>
          </div>
        </div>
      )}

      {shellMode === 'reorganized' ? (
        <TemplateBuilderReorganizedShell
          activeDocTypeFilter={activeDocTypeFilter}
          activeModuleFilter={activeModuleFilter}
          activeSearchFilter={activeSearchFilter}
          clearAllFilters={clearAllFilters}
          diagnostics={templateDiagnostics}
          documentTypeOptions={documentTypeOptions}
          getDocumentLabel={getDocumentLabel}
          getLocationName={getLocationName}
          getModuleMeta={getModuleMeta}
          getPrimaryTemplateModule={getPrimaryTemplateModule}
          getTemplateDecisionGuidance={getTemplateDecisionGuidance}
          getTemplateDisplayOptionLabel={getTemplateDisplayOptionLabel}
          moduleOptions={MODULE_GROUPS}
          onDeleteTemplate={handleDeleteTemplate}
          onOpenFreeformEditor={openFreeformEditor}
          onOpenPreviewDialog={() => setPreviewDialogOpen(true)}
          onPreviewTemplate={(template) => {
            if (template?.freeform_layout) {
              handlePreviewPDF(null, { openDialog: false, templateOverride: template, useStoredLayout: true });
              return;
            }
            toast.info('Abra o editor e adicione elementos primeiro');
          }}
          previewError={previewError}
          previewLoading={previewLoading}
          previewMeta={previewMeta}
          previewUrl={previewMeta?.sourceTemplateId === selectedTemplate?.id ? previewUrl : null}
          onSelectTemplate={handleSelectTemplate}
          onSetDefaultTemplate={handleSetDefault}
          selectedLayout={selectedTemplate ? getConformedTemplateLayout(selectedTemplate) : null}
          selectedTemplate={selectedTemplate}
          sortedTemplates={sortedTemplates}
          templateStats={templateStats}
          updateSearchFilter={updateSearchFilter}
          formatDateTime={formatDateTime}
        />
      ) : (
      <div className="grid min-h-[720px] grid-cols-12 gap-4 xl:h-[calc(100vh-230px)]">
        {/* Templates List */}
        <Card className="col-span-5 h-full">
          <CardHeader className="py-2.5">
            <CardTitle className="text-sm flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <LayoutTemplate className="h-4 w-4" /> Templates Disponíveis
              </span>
              <Badge variant="outline" data-testid="templates-visible-count-badge">
                {sortedTemplates.length} / {templates.length}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="space-y-2.5 border-b px-3 py-2.5" data-testid="template-filter-panel">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Filtros verificáveis</Label>
                  <p className="text-xs text-muted-foreground" data-testid="template-filter-summary">
                    {sortedTemplates.length} template(s) visíveis com filtros módulo/doc_type.
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-xs"
                  onClick={clearAllFilters}
                  data-testid="clear-template-filters-btn"
                >
                  Limpar filtros
                </Button>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Selector manual de template</Label>
                <Select
                  value={selectedTemplate?.id || 'NONE'}
                  onValueChange={(value) => {
                    if (value === 'NONE') return;
                    const template = allTemplatesSorted.find((item) => item.id === value) || templates.find((item) => item.id === value);
                    if (template) {
                      handleSelectTemplate(template);
                    }
                  }}
                >
                  <SelectTrigger data-testid="template-manual-selector">
                    <SelectValue placeholder="Escolher template exacto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">Escolher template exacto</SelectItem>
                    {allTemplatesSorted.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        {getTemplateDisplayOptionLabel(template)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs text-muted-foreground">Pesquisa operacional</Label>
                <Input
                  value={activeSearchFilter}
                  onChange={(event) => updateSearchFilter('q', event.target.value)}
                  placeholder="Nome, doc type, módulo, escopo global ou ID"
                  data-testid="template-search-filter-input"
                />
              </div>

              <div className="grid grid-cols-1 gap-2" data-testid="template-filters-grid">
                <div>
                  <Label className="text-xs text-muted-foreground">Módulo</Label>
                  <Select
                    value={activeModuleFilter}
                    onValueChange={(value) => updateSearchFilter('module_group', value)}
                  >
                    <SelectTrigger data-testid="template-module-filter-select">
                      <SelectValue placeholder="Todos os módulos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">Todos os módulos</SelectItem>
                      {MODULE_GROUPS.map((moduleItem) => (
                        <SelectItem key={`filter-module-${moduleItem.value}`} value={moduleItem.value}>
                          {moduleItem.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground">Doc Type</Label>
                  <Select
                    value={activeDocTypeFilter}
                    onValueChange={(value) => updateSearchFilter('doc_type', value)}
                  >
                    <SelectTrigger data-testid="template-doc-type-filter-select">
                      <SelectValue placeholder="Todos os doc_types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">Todos os doc_types</SelectItem>
                      {documentTypeOptions.map((item) => (
                        <SelectItem key={`filter-doc-type-${item.value}`} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

              </div>

              <div className="flex flex-wrap items-center gap-2" data-testid="template-active-filters-row">
                <Badge variant="secondary" data-testid="active-module-filter-badge">
                  Módulo: {activeModuleFilter === 'ALL' ? 'TODOS' : activeModuleFilter}
                </Badge>
                <Badge variant="secondary" data-testid="active-doc-type-filter-badge">
                  Doc Type: {activeDocTypeFilter === 'ALL' ? 'TODOS' : activeDocTypeFilter}
                </Badge>
                <Badge variant="secondary" data-testid="active-location-filter-badge">
                  Escopo: GLOBAL POR DOC_TYPE
                </Badge>
                <Badge variant="secondary" data-testid="active-search-filter-badge">
                  Pesquisa: {activeSearchFilter.trim() ? activeSearchFilter : 'SEM FILTRO'}
                </Badge>
              </div>
            </div>
            <div className="border-b bg-muted/40 px-3 py-1.5" data-testid="template-table-header">
              <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1.2fr)] gap-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <span>Template</span>
                <span>Usado em</span>
                <span>Doc Type</span>
                <span>Escopo</span>
                <span>Estado / updated_at</span>
              </div>
            </div>
            <ScrollArea className="h-[calc(100vh-342px)]">
              <div className="space-y-2 px-3 py-2">
                {sortedTemplates.map((template) => {
                  const locationName = getLocationName(template);
                  const branchLabel = 'GLOBAL';
                  const moduleMeta = getModuleMeta(getPrimaryTemplateModule(template));
                  const decisionGuidance = getTemplateDecisionGuidance(template);
                  return (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => handleSelectTemplate(template)}
                      className={`
                        w-full rounded-lg border p-2 text-left transition-colors
                        ${selectedTemplate?.id === template.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-border hover:bg-muted/40'
                        }
                      `}
                      data-testid={`template-item-${template.id}`}
                    >
                      <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1.2fr)] gap-3 items-start">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-[13px] font-medium leading-tight" data-testid={`template-name-${template.id}`}>
                              {template.name}
                            </span>
                            {template.is_default && <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />}
                          </div>
                          <p className="mt-1 truncate text-[11px] text-muted-foreground" data-testid={`template-signature-${template.id}`}>
                            {getTemplateDisplayOptionLabel(template)} · {getDocumentLabel(template.document_type)} · {moduleMeta.label}
                          </p>
                          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            {template.is_active === false && (
                              <Badge variant="destructive" className="text-[10px]" data-testid={`template-inactive-${template.id}`}>
                                Inativo
                              </Badge>
                            )}
                            {isTemplateUnlinked(template) && (
                              <Badge variant="secondary" className="text-[10px]" data-testid={`template-unlinked-${template.id}`}>
                                Sem ligação
                              </Badge>
                            )}
                            {(template.elements_count || template.freeform_layout?.elements?.length || 0) > 0 && (
                              <Badge variant="secondary" className="text-[10px]" data-testid={`template-elements-${template.id}`}>
                                <Layers className="mr-1 h-3 w-3" />
                                {template.elements_count || template.freeform_layout?.elements?.length || 0} elementos
                              </Badge>
                            )}
                            {decisionGuidance && (
                              <Badge
                                variant="secondary"
                                className={`text-[10px] ${decisionGuidance.decision === 'keep' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'}`}
                                data-testid={`template-manual-decision-${template.id}`}
                              >
                                {decisionGuidance.decision === 'keep' ? 'Manter' : 'Redundante'}
                              </Badge>
                            )}
                            {template.is_module_legacy && (
                              <Badge variant="secondary" className="text-[10px] bg-amber-100 text-amber-900" data-testid={`template-legacy-module-${template.id}`}>
                                Módulo legacy
                              </Badge>
                            )}
                            {template.is_location_legacy && (
                              <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-800" data-testid={`template-legacy-location-${template.id}`}>
                                Legado localização
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="min-w-0" data-testid={`template-module-column-${template.id}`}>
                          <Badge
                            className={`text-[10px] ${moduleMeta.badgeClass}`}
                            data-testid={`template-module-badge-${template.id}`}
                          >
                            {moduleMeta.label}
                          </Badge>
                        </div>

                        <div className="min-w-0" data-testid={`template-doc-type-cell-${template.id}`}>
                          <div className="text-sm font-medium">{getDocumentLabel(template.document_type)}</div>
                          <div className="text-[11px] text-muted-foreground" data-testid={`template-doc-type-${template.id}`}>
                            {template.document_type}
                          </div>
                        </div>

                        <div className="min-w-0" data-testid={`template-location-cell-${template.id}`}>
                          <div className="truncate text-sm font-medium" data-testid={`template-location-${template.id}`}>
                            Global dinâmico
                          </div>
                          <Badge variant="secondary" className="mt-2 text-[10px]" data-testid={`template-branch-${template.id}`}>
                            {branchLabel}
                          </Badge>
                        </div>

                        <div className="min-w-0" data-testid={`template-status-column-${template.id}`}>
                          <div className="flex flex-wrap gap-2">
                            <Badge variant={template.is_default ? 'default' : 'secondary'} data-testid={`template-default-${template.id}`}>
                              {template.is_default ? 'Padrão' : 'Secundário'}
                            </Badge>
                            <Badge variant={template.is_active === false ? 'destructive' : 'outline'} data-testid={`template-status-${template.id}`}>
                              {template.is_active === false ? 'Inactivo' : 'Activo'}
                            </Badge>
                          </div>
                          {(template.updated_at || template.created_at) && (
                            <div className="mt-2 text-[11px] text-muted-foreground" data-testid={`template-updated-${template.id}`}>
                              {formatDateTime(template.updated_at || template.created_at)}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}

                {sortedTemplates.length === 0 && (
                  <p className="rounded-md border border-dashed px-3 py-6 text-center text-xs text-muted-foreground" data-testid="template-empty-state">
                    Nenhum template encontrado para os filtros actuais.
                  </p>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Template Details & Actions */}
        <Card className="col-span-7 h-full">
          {!selectedTemplate ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <LayoutTemplate className="h-16 w-16 mb-4 opacity-30" />
              <p className="text-lg font-medium">Seleccione um Template</p>
              <p className="text-sm">ou crie um novo para começar</p>
            </div>
          ) : (
            <>
              {getTemplateDecisionGuidance(selectedTemplate) && (
                <div
                  className={`mx-6 mt-6 rounded-lg border px-4 py-4 ${getTemplateDecisionGuidance(selectedTemplate).decision === 'keep' ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}
                  data-testid="selected-template-manual-decision-panel"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold">Decisão guiada para este template</h3>
                      <p className="text-xs text-muted-foreground" data-testid="selected-template-manual-decision-scope">
                        {getTemplateDecisionGuidance(selectedTemplate).scope}
                      </p>
                    </div>
                    <Badge
                      className={getTemplateDecisionGuidance(selectedTemplate).decision === 'keep' ? 'bg-emerald-600 text-white' : 'bg-amber-600 text-white'}
                      data-testid="selected-template-manual-decision-badge"
                    >
                      {getTemplateDecisionGuidance(selectedTemplate).label}
                    </Badge>
                  </div>
                  <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Template pareado</Label>
                      <p className="text-sm font-medium" data-testid="selected-template-manual-decision-counterpart">
                        {getTemplateDecisionGuidance(selectedTemplate).counterpartName}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Execução</Label>
                      <p className="text-sm font-medium" data-testid="selected-template-manual-decision-execution">
                        Revisão manual posterior no Template Builder
                      </p>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground" data-testid="selected-template-manual-decision-note">
                    {getTemplateDecisionGuidance(selectedTemplate).note}
                  </p>
                </div>
              )}
              <CardHeader className="border-b px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="truncate text-lg">{selectedTemplate.name}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {getDocumentLabel(selectedTemplate.document_type)}
                    </p>
                    <p className="mt-1 text-[11px] text-muted-foreground" data-testid="selected-template-header-signature">
                      {getResolvedTemplateSignature(selectedTemplate, selectedTemplateName, selectedTemplateModules)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleSetDefault(selectedTemplate.id)}
                      title="Definir como padrão"
                      data-testid="set-default-selected-template-btn"
                    >
                      <Star className={`h-4 w-4 ${selectedTemplate?.is_default ? 'text-yellow-500 fill-yellow-500' : ''}`} />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleReplaceTemplateFlow}
                      data-testid="replace-selected-template-btn"
                    >
                      Substituir Template
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteTemplate(selectedTemplate.id)}
                      title="Eliminar"
                      data-testid="delete-selected-template-btn"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-5">
                <div className="space-y-4">
                  {/* Quick Info */}
                  <div className="grid grid-cols-2 gap-3 rounded-lg bg-muted/40 p-3 xl:grid-cols-6" data-testid="selected-template-overview-grid">
                    <div>
                      <Label className="text-xs text-muted-foreground">Tipo</Label>
                      <p className="font-medium" data-testid="selected-template-type">
                        {getDocumentLabel(selectedTemplate.document_type)}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Escopo</Label>
                      <p className="font-medium" data-testid="selected-template-branch">
                        Global por tipo de documento
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Módulo</Label>
                      <div className="mt-1" data-testid="selected-template-module">
                        <Badge className={getModuleMeta(getPrimaryTemplateModule(selectedTemplate)).badgeClass}>
                          {getPrimaryTemplateModule(selectedTemplate)}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Elementos</Label>
                      <p className="font-medium" data-testid="selected-template-elements-count">
                        {selectedTemplate.freeform_layout?.elements?.length || 0}
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Estado</Label>
                      <div className="font-medium mt-1 flex flex-wrap gap-2" data-testid="selected-template-status-badges">
                        <Badge variant={selectedTemplate.is_default ? 'default' : 'secondary'}>
                          {selectedTemplate.is_default ? 'Padrão' : 'Secundário'}
                        </Badge>
                        <Badge variant={selectedTemplate.is_active === false ? 'destructive' : 'outline'}>
                          {selectedTemplate.is_active === false ? 'Inactivo' : 'Activo'}
                        </Badge>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Actualizado</Label>
                      <p className="font-medium" data-testid="selected-template-updated-at">
                        {formatDateTime(selectedTemplate.updated_at || selectedTemplate.created_at)}
                      </p>
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-lg border bg-white" data-testid="template-identity-grid">
                    <div className="border-b p-3" data-testid="template-name-editor">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <h3 className="font-semibold text-sm">Nome final do template</h3>
                        <Button
                          size="sm"
                          onClick={handleSaveTemplateName}
                          data-testid="save-template-name-btn"
                        >
                          Guardar nome
                        </Button>
                      </div>
                      <div className="grid gap-2 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-end">
                        <div>
                          <Label className="text-xs text-muted-foreground">Nome visível</Label>
                          <Input
                            value={selectedTemplateName}
                            onChange={(event) => setSelectedTemplateName(event.target.value)}
                            placeholder="Ex.: Factura Vendas"
                            data-testid="selected-template-name-input"
                          />
                        </div>
                        <Badge variant="secondary" className="justify-center px-3 py-2" data-testid="selected-template-name-preview-badge">
                          {getResolvedTemplateName(selectedTemplate, selectedTemplateName)}
                        </Badge>
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground" data-testid="selected-template-name-note">
                        O nome é editável e os sufixos antigos como <strong>Global</strong> e <strong>Padrão 2026</strong> já não são mantidos nos templates finais.
                      </p>
                    </div>

                    <div className="border-b p-3" data-testid="template-module-group-editor">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <h3 className="font-semibold text-sm">Mapeamento verificável do módulo</h3>
                        <Button
                          size="sm"
                          onClick={handleSaveTemplateModuleGroup}
                          data-testid="save-template-module-group-btn"
                        >
                          Guardar módulo
                        </Button>
                      </div>
                      <div className="grid gap-2 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-end">
                        <div>
                          <Label className="text-xs text-muted-foreground">module_group</Label>
                          <Select
                            value={normalizeModuleGroup(selectedTemplateModules, selectedTemplate.document_type)[0]}
                            onValueChange={(value) => setSelectedTemplateModules([value])}
                          >
                            <SelectTrigger data-testid="selected-template-module-select">
                              <SelectValue placeholder="Seleccionar módulo" />
                            </SelectTrigger>
                            <SelectContent>
                              {MODULE_GROUPS.map((moduleItem) => (
                                <SelectItem key={`template-module-${moduleItem.value}`} value={moduleItem.value}>
                                  {moduleItem.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <Badge
                          className={`${getModuleMeta(normalizeModuleGroup(selectedTemplateModules, selectedTemplate.document_type)[0]).badgeClass} justify-center px-3 py-2`}
                          data-testid="selected-template-module-preview-badge"
                        >
                          {normalizeModuleGroup(selectedTemplateModules, selectedTemplate.document_type)[0]}
                        </Badge>
                      </div>
                      <p className="mt-2 text-xs text-muted-foreground" data-testid="selected-template-module-note">
                        O mapeamento efectivo controla a resolução do template no runtime por doc_type + módulo.
                      </p>
                      <div className="mt-3 grid gap-2 md:grid-cols-2" data-testid="selected-template-contract-governance-grid">
                        <div className="rounded-lg border bg-slate-50 p-3" data-testid="selected-template-document-family-card">
                          <Label className="text-xs text-muted-foreground">Família documental</Label>
                          <p className="mt-1 text-sm font-semibold">{selectedTemplate.document_family || selectedTemplate.document_type}</p>
                        </div>
                        <div className="rounded-lg border bg-slate-50 p-3" data-testid="selected-template-origin-context-card">
                          <Label className="text-xs text-muted-foreground">Origin context</Label>
                          <p className="mt-1 text-sm font-semibold">{selectedTemplate.origin_context || getPrimaryTemplateModule(selectedTemplate)}</p>
                        </div>
                      </div>
                      {(selectedTemplate.is_module_legacy || selectedTemplate.is_location_legacy) && (
                        <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-xs text-amber-900" data-testid="selected-template-legacy-warning">
                          {selectedTemplate.is_module_legacy && <p>Este template mantém `module_group` legacy no registo físico, mas o cockpit já usa o contexto efectivo governado.</p>}
                          {selectedTemplate.is_location_legacy && <p className="mt-1">Este template também está classificado como legado por localização e fica oculto por defeito fora do diagnóstico.</p>}
                        </div>
                      )}
                    </div>
                    <div className="bg-slate-50/80 p-3" data-testid="selected-template-resolution-card">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Assinatura final verificável</Label>
                          <p className="mt-1 text-sm font-semibold leading-snug" data-testid="selected-template-resolution-signature">
                            {getResolvedTemplateSignature(selectedTemplate, selectedTemplateName, selectedTemplateModules)}
                          </p>
                        </div>
                        <Badge variant="outline" data-testid="selected-template-resolution-scope-badge">
                          Global por doc_type
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {isA4EditorialDocType(selectedTemplate.document_type) && (
                    <TemplateDocTypeEditorialPanel
                      previewMeta={previewMeta}
                      selectedLayout={selectedTemplate ? getConformedTemplateLayout(selectedTemplate) : null}
                      selectedTemplate={selectedTemplate}
                    />
                  )}

                  {['pos_receipt', 'pos_gaming'].includes(selectedTemplate.document_type) && (
                    <div className="space-y-2.5" data-testid="selected-template-pos-structure-section">
                      <div>
                        <h3 className="font-semibold text-sm">Editor estrutural POS / talão curto</h3>
                        <p className="text-xs text-muted-foreground" data-testid="selected-template-pos-structure-copy">
                          Ajuste a tabela de itens/serviços e o quadro resumo de impostos com precisão em milímetros, com compactação forte e sem depender de templates por loja.
                        </p>
                      </div>
                      <PosStructureEditor
                        templateId={selectedTemplate.id}
                        layout={selectedTemplate.freeform_layout}
                        onSave={handleSaveLayout}
                        onPreview={handlePreviewPDF}
                      />
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="space-y-2.5">
                    <h3 className="font-semibold text-sm">Acções</h3>
                    <div className="grid grid-cols-1 gap-2 xl:grid-cols-2">
                      <Button 
                        className="h-14 items-center justify-center gap-2"
                        onClick={() => openFreeformEditor(selectedTemplate)}
                        data-testid="open-freeform-editor-btn"
                      >
                        <PenTool className="h-4 w-4" />
                        <span>Abrir Editor Freeform</span>
                      </Button>
                      <Button 
                        variant="outline"
                        className="h-14 items-center justify-center gap-2"
                        onClick={async () => {
                          if (selectedTemplate.freeform_layout) {
                            await handlePreviewPDF(selectedTemplate.freeform_layout);
                          } else {
                            toast.info('Abra o editor e adicione elementos primeiro');
                          }
                        }}
                        data-testid="preview-template-btn"
                      >
                        <Eye className="h-4 w-4" />
                        <span>Pré-visualizar PDF</span>
                      </Button>
                    </div>
                  </div>

                  {/* Layout JSON Preview */}
                  {selectedTemplate.freeform_layout?.elements?.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-sm mb-2">Layout JSON (Resumo)</h3>
                      <pre className="p-3 bg-muted rounded text-xs overflow-auto max-h-48" data-testid="layout-json-preview">
{JSON.stringify({
  canvasWidth: getConformedTemplateLayout(selectedTemplate)?.canvasWidth,
  canvasHeight: getConformedTemplateLayout(selectedTemplate)?.canvasHeight,
  elements: (getConformedTemplateLayout(selectedTemplate)?.elements || []).map(el => ({
    id: el.id,
    type: el.type,
    x: Math.round(el.x),
    y: Math.round(el.y),
    ...(el.variablePath && { variablePath: el.variablePath }),
    ...(el.datasource && { datasource: el.datasource }),
  }))
}, null, 2)}
                      </pre>
                    </div>
                  )}

                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
      )}

      {/* Create Template Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar Novo Template</DialogTitle>
            <DialogDescription>
              Configure um novo template de documento
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome do Template</Label>
              <Input
                value={templateForm.name}
                onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                placeholder="Ex: Guia de Recepção Personalizada"
                data-testid="create-template-name-input"
              />
            </div>
            <div>
              <Label>Tipo de Documento</Label>
              <Select
                value={templateForm.document_type}
                onValueChange={(v) => setTemplateForm({
                  ...templateForm,
                  document_type: v,
                  module_group: [getDefaultModuleGroupForDocType(v)],
                })}
              >
                <SelectTrigger data-testid="create-template-doc-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map((dt) => (
                    <SelectItem key={dt.value} value={dt.value}>
                      <div className="flex items-center gap-2">
                        <dt.icon className="h-4 w-4" />
                        {dt.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Módulo (Usado em)</Label>
              <Select
                value={normalizeModuleGroup(templateForm.module_group, templateForm.document_type)[0]}
                onValueChange={(value) => setTemplateForm({ ...templateForm, module_group: [value] })}
              >
                <SelectTrigger data-testid="create-template-module-select">
                  <SelectValue placeholder="Selecionar módulo" />
                </SelectTrigger>
                <SelectContent>
                  {MODULE_GROUPS.map((moduleItem) => (
                    <SelectItem key={`create-module-${moduleItem.value}`} value={moduleItem.value}>
                      {moduleItem.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-lg border bg-muted/30 px-3 py-2 text-sm text-muted-foreground" data-testid="create-template-unique-scope-note">
              Este template será o padrão do seu escopo único: <strong>1 template global por doc_type</strong>. Os dados da loja entram dinamicamente pela sessão activa.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateTemplate} data-testid="confirm-create-template-btn">
              <Plus className="h-4 w-4 mr-2" />
              Criar Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Template Impact Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) {
            setDeleteTargetTemplate(null);
            setDeleteImpact(null);
            setDeleteImpactLoading(false);
          }
        }}
      >
        <DialogContent data-testid="delete-template-impact-dialog">
          <DialogHeader>
            <DialogTitle>Confirmar eliminação de template</DialogTitle>
            <DialogDescription>
              Esta ação faz hard delete real na base de dados. Valide o impacto antes de confirmar.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <div className="p-3 border rounded" data-testid="delete-template-impact-summary">
              <p><strong>Template:</strong> {deleteTargetTemplate?.name || 'N/A'}</p>
              <p><strong>Doc Type:</strong> {deleteTargetTemplate?.document_type || 'N/A'}</p>
              <p><strong>Escopo:</strong> Global por doc_type</p>
              <p><strong>Módulo:</strong> {deleteTargetTemplate ? getPrimaryTemplateModule(deleteTargetTemplate) : 'N/A'}</p>
              {deleteImpactLoading ? (
                <p className="text-muted-foreground mt-2" data-testid="delete-impact-loading">A calcular impacto...</p>
              ) : (
                <>
                  <p data-testid="delete-impact-documents">
                    <strong>Documentos afetados:</strong>{' '}
                    {deleteImpact?.documents_count_deterministic
                      ? deleteImpact?.documents_count ?? 0
                      : 'Impacto: desconhecido'}
                  </p>
                  <p data-testid="delete-impact-modules">
                    <strong>Módulos afetados:</strong>{' '}
                    {Array.isArray(deleteImpact?.module_group) && deleteImpact.module_group.length > 0
                      ? deleteImpact.module_group.join(', ')
                      : 'Impacto: desconhecido'}
                  </p>
                  <p data-testid="delete-impact-active-count">
                    <strong>Templates ativos neste escopo:</strong>{' '}
                    {typeof deleteImpact?.active_templates_count === 'number'
                      ? deleteImpact.active_templates_count
                      : 'Impacto: desconhecido'}
                  </p>
                  <p data-testid="delete-impact-alternative-count">
                    <strong>Alternativas disponíveis:</strong>{' '}
                    {typeof deleteImpact?.alternatives_count === 'number'
                      ? deleteImpact.alternatives_count
                      : 'Impacto: desconhecido'}
                  </p>
                  <p data-testid="delete-impact-scope">
                    <strong>Escopo protegido:</strong>{' '}
                    {deleteImpact?.scope_label || 'Impacto: desconhecido'}
                  </p>
                </>
              )}
            </div>

            {!deleteImpactLoading && deleteImpact?.can_delete === false && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-red-900" data-testid="delete-template-blocked-warning">
                <p className="font-semibold">Eliminação bloqueada</p>
                <p>{deleteImpact?.blocking_message || 'Último template activo neste escopo. Use Substituir Template.'}</p>
                <p className="text-xs mt-1">
                  Este é o último template activo para este doc_type global.
                </p>
              </div>
            )}

            {!deleteImpactLoading && deleteImpact?.can_delete && deleteImpact?.will_reassign_default && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-950" data-testid="delete-template-default-reassignment-info">
                <p className="font-semibold">Default será redefinido automaticamente</p>
                <p>
                  Ao eliminar este template, o sistema vai promover automaticamente{' '}
                  <strong>{deleteImpact?.replacement_template?.name || 'a alternativa ativa'}</strong> como novo default.
                </p>
              </div>
            )}

            {!deleteImpactLoading && deleteImpact?.can_delete && !deleteImpact?.will_reassign_default && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-900" data-testid="delete-template-allowed-info">
                <p className="font-semibold">Hard delete permitido</p>
                <p>Existe pelo menos uma alternativa activa neste mesmo escopo.</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              data-testid="cancel-delete-template-btn"
            >
              Cancelar
            </Button>
            <Button
              variant="secondary"
              onClick={handleReplaceTemplateFlow}
              data-testid="replace-template-flow-btn"
            >
              Substituir Template
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDeleteTemplate}
              disabled={deleteImpactLoading || deleteImpact?.can_delete === false}
              data-testid="confirm-delete-template-btn"
            >
              Confirmar eliminação
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selectedTemplate && (
        <TemplateStoredEffectiveComparator
          compareData={templateCompareData}
          loading={templateCompareLoading}
        />
      )}

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-5xl h-[90vh]">
          <DialogHeader>
            <DialogTitle>Pré-visualização PDF</DialogTitle>
            <DialogDescription>
              {selectedTemplate?.name} - {previewLoading ? 'A gerar...' : 'Documento validado no mesmo pipeline de runtime/PDF'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 h-full min-h-[500px]">
            {previewLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
              </div>
            ) : previewUrl ? (
              <iframe
                src={previewUrl}
                className="w-full h-full border rounded"
                title="PDF Preview"
                data-testid="template-builder-preview-dialog-frame"
              />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground" data-testid="template-builder-preview-dialog-empty-state">
                {previewError || 'Erro ao carregar pré-visualização'}
              </div>
            )}
          </div>
          <DialogFooter>
            {previewUrl && (
              <div className="mr-auto flex flex-wrap gap-2">
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-md border px-3 py-2 text-sm font-medium transition-colors hover:bg-muted"
                  data-testid="preview-open-new-tab-link"
                >
                  Abrir em nova aba
                </a>
                <a
                  href={previewUrl}
                  download={`${selectedTemplate?.name || 'template-preview'}.pdf`}
                  className="inline-flex items-center justify-center rounded-md border px-3 py-2 text-sm font-medium transition-colors hover:bg-muted"
                  data-testid="preview-download-link"
                >
                  Descarregar PDF
                </a>
              </div>
            )}
            <Button variant="outline" onClick={() => setPreviewDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
